import { useContext, useState } from "react";
import { isValidEmail, toast } from "../../helper/common";
import { sendUniversalMail } from "../../services/sharedService";
import GuarantorInviteSection from "../../components/GuarantorInviteSection/GuarantorInviteSection";
import BasicInfoModal from "../onboarding/BasicInfoModal";
import RequiredDocumentsModal from "../onboarding/RequiredDocumentsModal";
import TenantChecksIncomeModel from "./TenantChecksIncomeModel";
import ApplicationContext from "../../context/application/ApplicationContext";
import TenantContext from "../../context/tenant/TenantContext";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import {
  Avatar,
  Badge,
  Button,
  Icon,
  TextField,
} from "../../components/design";
import "./tenantCheckSteps.css";

export default function TenantChecksGroupStep({
  user,
  formData,
  guarantor,
  guarantorFormData,
  setGuarantorFormData,
  createUserProfile,
  requiresSubcategory,
  originalUser,
  guarantorPhoneNumber,
  setGuarantorPhoneNumber,
  selectedProtectedHousingDocs,
  setSelectedProtectedHousingDocs,
  additionalInfo,
  formHandler,
  setStepId,
  updateLLTenant,
  completeTenantCheck,
  loadData,
  _t,
}) {
  const { createApplication } = useContext(ApplicationContext);
  const { createBasicGuarantorAccount, upsertSelectedOnboardingUsers } =
    useContext(TenantContext);
  const [guarantorStage, setGuarantorStage] = useState("overview");
  const [guarantorInvites, setGuarantorInvites] = useState([]);
  const [guarantorSubmitting, setGuarantorSubmitting] = useState(false);
  const [isEditingTenant, setIsEditingTenant] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // tenant invites — same as CreateApplicationStep
  const [tenantInvites, setTenantInvites] = useState([]);
  const [openTenantForm, setOpenTenantForm] = useState(false);
  const [tenantDraft, setTenantDraft] = useState({
    name: "",
    email: "",
    error: "",
  });

  const tenantName =
    [user?.fname, user?.lname].filter(Boolean).join(" ") ||
    [formData?.fname, formData?.lname].filter(Boolean).join(" ") ||
    _t("tenant", "Tenant");
  const tenantEmail = user?.email || formData?.email || "";

  const startEditTenant = () => {
    setEditName(tenantName === _t("tenant", "Tenant") ? "" : tenantName);
    setEditEmail(tenantEmail);
    setIsEditingTenant(true);
  };
  const saveTenantEdit = async () => {
    const name = editName.trim();
    const email = editEmail.trim().toLowerCase();
    if (!name || !email) {
      toast.error(_t("nameAndEmailRequired", "Name and email are required"));
      return;
    }
    const [fname = "", ...rest] = name.split(/\s+/);
    const lname = rest.join(" ");
    try {
      setEditSaving(true);
      await updateLLTenant({ id: user?._id, fname, lname, email });
      await loadData(false, user?._id);
      setIsEditingTenant(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || _t("somethingWentWrong"));
    } finally {
      setEditSaving(false);
    }
  };

  const addTenantInvite = () => {
    const trimmedName = tenantDraft.name.trim();
    const trimmedEmail = tenantDraft.email.trim().toLowerCase();
    if (!trimmedName) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("nameRequired", "Full name is required"),
      }));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("invalidEmail", "Enter a valid email address"),
      }));
      return;
    }
    if (
      trimmedEmail === user?.email?.toLowerCase() ||
      trimmedEmail === originalUser?.email?.toLowerCase()
    ) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("cannotAddYourself", "You cannot add yourself"),
      }));
      return;
    }
    if (
      tenantInvites.some((i) => i.email === trimmedEmail) ||
      guarantorInvites.some((g) => g.email === trimmedEmail)
    ) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("emailAlreadyAdded", "This email has already been added"),
      }));
      return;
    }
    if (tenantInvites.length >= 4) {
      setTenantDraft((c) => ({
        ...c,
        error: _t("maxParticipantsReached", "Maximum of 4 tenants allowed"),
      }));
      return;
    }
    setTenantInvites((c) => [...c, { name: trimmedName, email: trimmedEmail }]);
    setTenantDraft({ name: "", email: "", error: "" });
    setOpenTenantForm(false);
  };

  const submitGuarantors = async (invitesToSubmit) => {
    const emailInvites = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod !== "self"
    );
    const selfManaged = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod === "self"
    );
    for (const g of emailInvites) {
      const [fname, ...surname] = g.name.split(/\s+/);
      try {
        await createBasicGuarantorAccount({
          fname,
          lname: surname.join(" "),
          email: g.email,
          gurantor_relation: g.relation,
          step: 1,
        });
      } catch (err) {
        if (err?.response?.data?.errorKey !== "account-already-present")
          throw err;
      }
      await sendUniversalMail({
        receiverEmail: g.email,
        senderId: localStorage.getItem("saveduserid"),
        emailType: "score",
        status: "processing",
        gurantor_relation: g.relation,
      });
    }
    if (selfManaged.length > 0) {
      const selectedOnboardingUsers = Array.isArray(
        user?.tenant_extra_field_id?.selected_onboarding_users
      )
        ? user.tenant_extra_field_id.selected_onboarding_users
        : [];
      const newUsers = selfManaged
        .map(({ name, email, relation }) => ({
          name,
          email,
          type: "guarantor",
          gurantor_relation: relation,
        }))
        .filter(
          ({ name, type }) =>
            !selectedOnboardingUsers.some(
              (u) => u?.type === type && u?.name === name
            )
        );
      const next = [...selectedOnboardingUsers, ...newUsers];
      const result = await upsertSelectedOnboardingUsers({
        tenant_id: user?._id ?? localStorage.getItem("saveduserid"),
        users: next,
      });
      if (result === null) throw new Error("failed-to-add-guarantor");
    }
  };

  const continueFromGuarantorInvites = async () => {
    if (guarantor) {
      setGuarantorStage("documents");
      return;
    }
    const selfInvites = guarantorInvites.filter(
      ({ guarantorMethod }) => guarantorMethod === "self"
    );
    if (selfInvites.length > 1) {
      toast.error(
        _t(
          "onlyOneGuarantorCanBeCompletedHere",
          "Only one guarantor can be completed in this onboarding"
        )
      );
      return;
    }
    try {
      setGuarantorSubmitting(true);
      await submitGuarantors(guarantorInvites);
      if (selfInvites[0]) {
        const { name, email, relation } = selfInvites[0];
        const [fname = "", ...lastNameParts] = name.trim().split(/\s+/);
        setGuarantorFormData((current) => ({
          ...current,
          email,
          fname,
          lname: lastNameParts.join(" "),
          gurantor_relation: relation,
        }));
        setGuarantorStage("details");
        return;
      }
      if (tenantInvites.length > 0) {
        const title = `${tenantName} ${_t("application", "application")}`;
        await createApplication({
          participants: tenantInvites.map(({ name, email }) => ({
            name,
            email,
          })),
          title,
          skor_protect: true,
          description: "",
        });
      }
      await completeTenantCheck();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          _t("somethingWentWrong")
      );
    } finally {
      setGuarantorSubmitting(false);
    }
  };

  const advanceGuarantorDocuments = async () => {
    if (additionalInfo.includes("GUARANTOR_SKOR")) {
      setGuarantorStage("financial");
      return;
    }
    await completeTenantCheck();
  };
  const updateGuarantorProfile = async (data) =>
    updateLLTenant({ ...data, id: guarantor?._id });

  if (guarantorStage === "details") {
    return (
      <BasicInfoModal
        user={guarantor ?? {}}
        formData={guarantorFormData}
        setFormData={setGuarantorFormData}
        createUserProfile={(data) => createUserProfile(data, "llGuarantor")}
        formHandler={{
          prev: () => setGuarantorStage("overview"),
          next: () => setGuarantorStage("documents"),
        }}
        requiresSubcategory={requiresSubcategory}
        originalUser={originalUser}
        phoneNumber={guarantorPhoneNumber}
        setPhoneNumber={setGuarantorPhoneNumber}
        selectedProtectedHousingDocs={selectedProtectedHousingDocs}
        setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
        usedFor="tenantChecks"
        llGuarantor
      />
    );
  }
  if (guarantorStage === "documents" && guarantor) {
    return (
      <RequiredDocumentsModal
        user={guarantor}
        formHandler={{
          prev: () => setGuarantorStage("overview"),
          next: advanceGuarantorDocuments,
        }}
        updateUserProfile={advanceGuarantorDocuments}
        isGuarantor={false}
        needWaitingJobForClave={false}
        isEditMode={false}
        hideClave
        llCreated
        onClose={() => setGuarantorStage("overview")}
      />
    );
  }
  if (guarantorStage === "financial" && guarantor) {
    return (
      <TenantChecksIncomeModel
        user={guarantor}
        additionalInfo={[]}
        isGuarantor
        usedFor="tenantChecks"
        formHandler={{
          prev: () => setGuarantorStage("documents"),
          next: completeTenantCheck,
        }}
        loadGuarantorData={() => loadData(false, user?._id)}
        updateUserProfile={updateGuarantorProfile}
      />
    );
  }

  return (
    <div className="tc-step-page">
      <section className="tc-step">
        <AuthStepHeader
          onBack={formHandler.prev}
          title={_t("groupThisCheck", "Group this check")}
          subtitle={_t(
            "groupThisCheckSubtitle",
            "If this tenant comes with a guarantor or other tenants, add them here. You'll fill in their checks yourself."
          )}
        />

        {isEditingTenant ? (
          <div className="tc-inline-form">
            <div className="tc-field-row">
              <TextField
                id="tc-edit-name"
                label={_t("fullName", "Full name")}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoFocus
              />
              <TextField
                id="tc-edit-email"
                type="email"
                label={_t("emailAddress", "Email address")}
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div className="tc-inline-actions">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setIsEditingTenant(false)}
              >
                {_t("cancel", "Cancel")}
              </Button>
              <Button size="lg" loading={editSaving} onClick={saveTenantEdit}>
                {_t("save", "Save")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="tc-person">
            <Avatar name={tenantName} />
            <div className="tc-person__text">
              <div className="tc-person__name">
                {tenantName || "Javier Moreno"}
                <Badge tone="neutral">{_t("tenant", "Tenant")}</Badge>
              </div>
              <div className="tc-person__email">
                {tenantEmail || "javier.moreno@gmail.com"}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              iconStart={<Icon name="edit" size={14} />}
              onClick={startEditTenant}
            >
              {_t("edit", "Edit")}
            </Button>
          </div>
        )}

        <div className="tc-group-section">
          {guarantor ? (
            <div className="tc-person tc-person--muted">
              <Avatar
                name={
                  [guarantor.fname, guarantor.lname]
                    .filter(Boolean)
                    .join(" ") || "G"
                }
                size="sm"
              />
              <div className="tc-person__text">
                <div className="tc-person__name">
                  {[guarantor.fname, guarantor.lname].filter(Boolean).join(" ")}
                </div>
                <div className="tc-person__email">{guarantor.email}</div>
              </div>
            </div>
          ) : (
            <GuarantorInviteSection
              currentUserEmail={originalUser?.email}
              invites={guarantorInvites}
              onChange={setGuarantorInvites}
              _t={_t}
              guarantorOptionalKey="guarantorOptionalForCheck"
            />
          )}
        </div>

        {/* Tenant invites — same as CreateApplicationStep */}
        {tenantInvites.map((invite) => (
          <div key={invite.email} className="tc-person">
            <Avatar name={invite.name} />
            <div className="tc-person__text">
              <div className="tc-person__name">{invite.name}</div>
              <div className="tc-person__email">{invite.email}</div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              iconStart={<Icon name="close" size={16} />}
              aria-label={_t("removeTenant", "Remove tenant")}
              onClick={() =>
                setTenantInvites((c) =>
                  c.filter((i) => i.email !== invite.email)
                )
              }
            />
          </div>
        ))}

        {openTenantForm ? (
          <div className="tc-inline-form">
            <Badge tone="neutral">{_t("tenant", "Tenant")}</Badge>
            <div className="tc-field-row">
              <TextField
                id="tc-invite-name"
                label={_t("fullName", "Full name")}
                value={tenantDraft.name}
                autoFocus
                onChange={(e) =>
                  setTenantDraft((c) => ({
                    ...c,
                    name: e.target.value,
                    error: "",
                  }))
                }
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addTenantInvite())
                }
              />
              <TextField
                id="tc-invite-email"
                type="email"
                label={_t("emailAddress", "Email address")}
                value={tenantDraft.email}
                error={tenantDraft.error}
                onChange={(e) =>
                  setTenantDraft((c) => ({
                    ...c,
                    email: e.target.value.toLowerCase(),
                    error: "",
                  }))
                }
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addTenantInvite())
                }
                placeholder="email@example.com"
              />
            </div>
            <div className="tc-inline-actions">
              <Button
                variant="ghost"
                size="lg"
                iconOnly
                iconStart={<Icon name="trash" size={16} />}
                aria-label={_t("remove", "Remove")}
                onClick={() => {
                  setOpenTenantForm(false);
                  setTenantDraft({ name: "", email: "", error: "" });
                }}
              />
              <Button
                variant="secondary"
                size="lg"
                onClick={addTenantInvite}
                disabled={!tenantDraft.name.trim() || !tenantDraft.email.trim()}
              >
                {_t("add", "Add")}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            iconStart={<Icon name="plus" size={14} />}
            onClick={() => setOpenTenantForm(true)}
            disabled={tenantInvites.length >= 4}
          >
            {_t("addTenant", "Add tenant")}
          </Button>
        )}

        <div className="tc-step-actions">
          <Button
            size="lg"
            loading={guarantorSubmitting}
            onClick={continueFromGuarantorInvites}
          >
            {guarantorSubmitting
              ? _t("processing", "Processing…")
              : _t("finish", "Finish")}
          </Button>
        </div>
      </section>
    </div>
  );
}
